import { getAuthorPath } from 'utils/authors';
import { ORGANIZATION_ID } from 'utils/schema/organizationSchema';
import { trailingSlash } from 'utils/strings';
import { forceHttps } from 'utils/strings/slugs';

const SITE = 'https://prismatic.io';

// Builder hands back http:// Cloudinary URLs; schema consumers should never be
// pointed at an insecure asset.
const imageUrl = (image?: AuthorReference['avatar']) => forceHttps(image?.url || image?.file?.url) || undefined;

const absoluteUrl = (path: string) => trailingSlash(`${SITE}${path.startsWith('/') ? '' : '/'}${path}`);

/**
 * The Person node for an author, shared by the Article schema on every post and
 * the ProfilePage schema on the author's own page.
 *
 * When the author has a page, the node carries its URL and a stable `@id`, which
 * is what ties a post's byline to the profile for search engines and LLMs rather
 * than leaving a loose name string on each article.
 */
export const buildPersonNode = (data?: Maybe<AuthorReference>) => {
  if (!data?.name) {
    return undefined;
  }

  const sameAs = (data.socials || []).map(social => social?.link).filter((link): link is string => !!link);
  const avatar = imageUrl(data.avatar);
  const authorPath = getAuthorPath(data);
  const url = authorPath ? absoluteUrl(authorPath) : undefined;

  return {
    '@type': 'Person' as const,
    ...(url ? { '@id': `${url}#person` } : {}),
    name: data.name,
    ...(data.title ? { jobTitle: data.title } : {}),
    ...(data.bio ? { description: data.bio } : {}),
    ...(avatar ? { image: avatar } : {}),
    ...(url ? { url } : {}),
    ...(sameAs.length ? { sameAs } : {}),
  };
};

/**
 * ProfilePage + Person structured data for /blog/authors/<slug>. `worksFor`
 * points at the Organization node Head already emits site-wide, so the profile
 * resolves as a Prismatic employee rather than an unaffiliated person.
 */
export const getPersonSchema = (data?: Maybe<AuthorReference>) => {
  const person = buildPersonNode(data);
  const authorPath = getAuthorPath(data);

  if (!person || !authorPath) {
    return null;
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    '@id': absoluteUrl(authorPath),
    mainEntity: { ...person, worksFor: { '@id': ORGANIZATION_ID } },
  };
};
